import React from 'react';
import { node, object, shape, string } from 'prop-types';

import { NamedLink } from '../../../components';

/**
 * One link on a landing page.
 *
 * These pages used to be static HTML, so every link was a plain anchor and every
 * click cost a full document load. Now that they live inside the app, anything
 * pointing at an app route should route client-side instead - `NamedLink` also
 * means the URL is generated from the route table rather than hand-written, so
 * a path can no longer drift from its route.
 *
 * A descriptor is either:
 *   { name, params, to }  an app route, rendered as NamedLink
 *   { href }              anything else - same-page anchors, external URLs
 *
 * @param {Object} props
 * @param {Object} props.link the descriptor
 * @param {string} [props.className]
 * @param {React.Node} props.children
 */
const LandingLink = props => {
  const { link, className, children, ...rest } = props;

  if (!link) {
    return null;
  }

  // Same-page anchors (#faq) and anything outside the route table.
  if (link.href) {
    return (
      <a className={className} href={link.href} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <NamedLink className={className} name={link.name} params={link.params} to={link.to} {...rest}>
      {children}
    </NamedLink>
  );
};

LandingLink.propTypes = {
  link: shape({
    href: string,
    name: string,
    params: object,
    to: object,
  }),
  className: string,
  children: node,
};

export default LandingLink;
